import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Loader2, Pencil, Trash2, Shield, User, Ban, CheckCircle, SlidersHorizontal } from "lucide-react";
import { toast } from "@/lib/toastBanner";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return d;
  }
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "admin" });

  // Per-user hidden-features modal state (System Admin only)
  const [permsTarget, setPermsTarget] = useState(null);
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [hiddenSet, setHiddenSet] = useState(new Set());
  const [permsLoading, setPermsLoading] = useState(false);
  const [permsSaving, setPermsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const viewerIsSystemAdmin = user?.role === "system_admin";
  const roleOptions = viewerIsSystemAdmin
    ? [
        { value: "instructor", label: "Instructor" },
        { value: "admin", label: "Admin" },
        { value: "system_admin", label: "System Admin" },
      ]
    : [
        { value: "instructor", label: "Instructor" },
        { value: "admin", label: "Admin" },
      ];

  const fetchAdmins = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admins`, { withCredentials: true });
      setAdmins(data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Access denied");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const openAdd = () => {
    setEditTarget(null);
    setForm({ first_name: "", last_name: "", email: "", password: "", role: "admin" });
    setShowModal(true);
  };

  const openEdit = (admin) => {
    const parts = (admin.full_name || "").split(" ");
    setEditTarget(admin);
    setForm({
      first_name: parts[0] || "",
      last_name: parts.slice(1).join(" ") || "",
      email: admin.email || "",
      password: "",
      role: admin.role || "admin",
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) return;
    if (!editTarget && !form.password) {
      toast.error("Password is required for new admins");
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        const payload = {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          role: form.role,
        };
        if (form.password) payload.password = form.password;
        await axios.put(`${API}/admins/${editTarget.id}`, payload, { withCredentials: true });
        toast.success("Admin updated");
      } else {
        await axios.post(`${API}/admins`, {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          password: form.password,
          role: form.role,
        }, { withCredentials: true });
        toast.success("Admin created");
      }
      setShowModal(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const admin = deleteTarget;
    if (!admin) return;
    try {
      await axios.delete(`${API}/admins/${admin.id}`, { withCredentials: true });
      toast.success("Admin deleted");
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
      throw err;
    }
  };

  const handleToggleStatus = async (admin) => {
    const action = admin.status === "disabled" ? "enable" : "disable";
    if (!window.confirm(`${action === "disable" ? "Disable" : "Enable"} ${admin.full_name}?`)) return;
    try {
      await axios.patch(`${API}/admins/${admin.id}/toggle-status`, {}, { withCredentials: true });
      toast.success(`Admin ${action}d`);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const openPerms = async (admin) => {
    setPermsTarget(admin);
    setPermsLoading(true);
    try {
      const [catRes, currentRes] = await Promise.all([
        axios.get(`${API}/feature-catalog`, { withCredentials: true }),
        axios.get(`${API}/admins/${admin.id}/hidden-features`, { withCredentials: true }),
      ]);
      setFeatureCatalog(catRes.data?.features || []);
      setHiddenSet(new Set(currentRes.data?.hidden_features || []));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
    } finally {
      setPermsLoading(false);
    }
  };

  const togglePerm = (key) => {
    setHiddenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const savePerms = async () => {
    if (!permsTarget) return;
    setPermsSaving(true);
    try {
      await axios.put(
        `${API}/admins/${permsTarget.id}/hidden-features`,
        { hidden_features: Array.from(hiddenSet) },
        { withCredentials: true },
      );
      toast.success("Sidebar preferences saved");
      setPermsTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setPermsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="admin-users-title">
            Admin Users
          </h2>
          <p className="text-[#6E7787] font-body text-sm mt-1">
            Manage platform administrators
          </p>
        </div>
        <Button
          onClick={openAdd}
          data-testid="add-admin-btn"
          className="h-10 px-5 font-body font-medium text-white rounded-lg hover:opacity-90 shrink-0"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Add Admin
        </Button>
      </div>

      {admins.length === 0 ? (
        <div className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-12 text-center" data-testid="admin-users-empty">
          <Shield className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--primary-color, #4A90D9)" }} />
          <h3 className="font-body text-lg font-bold text-white mb-2">No admin users</h3>
        </div>
      ) : (
        <div className="bg-[#11141A] border border-white/10 rounded-xl overflow-hidden" data-testid="admin-users-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Name</th>
                  <th className="text-left px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Email</th>
                  <th className="text-left px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Role</th>
                  <th className="text-left px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Created</th>
                  <th className="text-right px-5 py-3.5 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => {
                  const isSelf = admin.id === user?.id;
                  const isSystemAdmin = admin.role === "system_admin";
                  const isInstructor = admin.role === "instructor";
                  const isDisabled = admin.status === "disabled";
                  const roleLabel = isSystemAdmin ? "System Admin" : isInstructor ? "Instructor" : "Admin";
                  const roleBadgeClass = isSystemAdmin
                    ? "bg-violet-500/15 text-violet-400 border-violet-500/20"
                    : isInstructor
                      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                      : "bg-sky-500/15 text-sky-400 border-sky-500/20";
                  return (
                    <tr
                      key={admin.id}
                      className={`border-b border-white/5 hover:bg-white/[0.02] transition-colors ${isDisabled ? "opacity-60" : ""}`}
                      data-testid={`admin-row-${admin.id}`}
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div
                            className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0"
                            style={{ backgroundColor: isSystemAdmin ? "var(--primary-color, #4A90D9)" : "#2A2D34" }}
                          >
                            {admin.full_name?.[0]?.toUpperCase() || "A"}
                          </div>
                          <span className="text-sm font-body text-white" data-testid={`admin-name-${admin.id}`}>
                            {admin.full_name}
                            {isSelf && <span className="text-xs text-[#6E7787] ml-1.5">(you)</span>}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm font-body text-[#A1A9B8]" data-testid={`admin-email-${admin.id}`}>
                        {admin.email}
                      </td>
                      <td className="px-5 py-3.5">
                        <Badge
                          data-testid={`admin-role-${admin.id}`}
                          className={roleBadgeClass}
                        >
                          {roleLabel}
                        </Badge>
                      </td>
                      <td className="px-5 py-3.5">
                        <Badge
                          data-testid={`admin-status-${admin.id}`}
                          className={
                            isDisabled
                              ? "bg-red-500/15 text-red-400 border-red-500/20"
                              : "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                          }
                        >
                          {isDisabled ? "Disabled" : "Enabled"}
                        </Badge>
                      </td>
                      <td className="px-5 py-3.5 text-sm font-body text-[#6E7787]">
                        {formatDate(admin.created_at)}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          {!isSelf && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEdit(admin)}
                                data-testid={`edit-admin-${admin.id}`}
                                className="h-7 w-7 p-0 text-[#6E7787] hover:text-white hover:bg-white/5"
                                title="Edit"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              {viewerIsSystemAdmin && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openPerms(admin)}
                                  data-testid={`manage-perms-${admin.id}`}
                                  className="h-7 w-7 p-0 text-[#6E7787] hover:text-white hover:bg-white/5"
                                  title="Manage Permissions"
                                >
                                  <SlidersHorizontal className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleStatus(admin)}
                                data-testid={`toggle-admin-${admin.id}`}
                                className={`h-7 w-7 p-0 ${isDisabled ? "text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10" : "text-[#6E7787] hover:text-amber-400 hover:bg-amber-500/10"}`}
                                title={isDisabled ? "Enable" : "Disable"}
                              >
                                {isDisabled ? <CheckCircle className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteTarget(admin)}
                                data-testid={`delete-admin-${admin.id}`}
                                className="h-7 w-7 p-0 text-[#6E7787] hover:text-red-400 hover:bg-red-500/10"
                                title="Delete"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Admin Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-md" data-testid="admin-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white">
              {editTarget ? "Edit Admin" : "Add Admin"}
            </DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body">
              {editTarget ? "Update admin details. Leave password blank to keep existing." : "Create a new admin account."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">First Name <span className="text-red-400">*</span></Label>
                <Input
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  required
                  data-testid="admin-first-name-input"
                  className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">Last Name <span className="text-red-400">*</span></Label>
                <Input
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  required
                  data-testid="admin-last-name-input"
                  className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Email <span className="text-red-400">*</span></Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="admin-email-input"
                className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">
                Password {!editTarget && <span className="text-red-400">*</span>}
              </Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required={!editTarget}
                placeholder={editTarget ? "Leave blank to keep existing" : ""}
                data-testid="admin-password-input"
                className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#A1A9B8] font-body text-sm">Role</Label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="admin-role-select"
                className="w-full h-10 rounded-md bg-[#0A0C10] border border-white/10 text-white text-sm font-body px-3 focus:outline-none focus:border-[var(--primary-color,#4A90D9)] transition-colors"
              >
                {roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowModal(false)}
                className="text-[#A1A9B8] hover:text-white hover:bg-white/5"
                data-testid="admin-modal-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="admin-modal-save"
                className="h-10 px-5 font-body font-medium text-white rounded-lg hover:opacity-90"
                style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
                {editTarget ? "Save Changes" : "Create Admin"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Manage Permissions Modal — cosmetic per-user sidebar hide */}
      <Dialog open={!!permsTarget} onOpenChange={(open) => !open && setPermsTarget(null)}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-lg" data-testid="perms-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-xl text-white">
              Manage Permissions
            </DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body">
              {permsTarget ? (
                <>Hide specific features from <span className="text-white">{permsTarget.full_name}</span>'s sidebar. This only affects what they see — their role and underlying permissions are unchanged.</>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          {permsLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
            </div>
          ) : (
            <div className="mt-2 max-h-[60vh] overflow-y-auto pr-1 space-y-2">
              {featureCatalog.length === 0 ? (
                <p className="text-sm text-[#6E7787] font-body py-4 text-center">No toggleable features.</p>
              ) : featureCatalog.map((f) => {
                const hidden = hiddenSet.has(f.key);
                return (
                  <label
                    key={f.key}
                    data-testid={`perm-toggle-${f.key}`}
                    className="flex items-center justify-between gap-4 px-3.5 py-3 rounded-lg border border-white/10 bg-[#0A0C10] hover:bg-white/[0.03] cursor-pointer transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-body text-white truncate">{f.label}</div>
                      <div className="text-xs text-[#6E7787] font-body mt-0.5">
                        {hidden ? "Hidden from sidebar" : "Visible in sidebar"}
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={!hidden}
                      onChange={() => togglePerm(f.key)}
                      className="h-4 w-4 shrink-0 accent-[var(--primary-color,#4A90D9)] cursor-pointer"
                      data-testid={`perm-checkbox-${f.key}`}
                    />
                  </label>
                );
              })}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4 border-t border-white/5 mt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPermsTarget(null)}
              className="text-[#A1A9B8] hover:text-white hover:bg-white/5"
              data-testid="perms-modal-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={savePerms}
              disabled={permsSaving || permsLoading}
              data-testid="perms-modal-save"
              className="h-10 px-5 font-body font-medium text-white rounded-lg hover:opacity-90"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
            >
              {permsSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        tier={1}
        entityName={deleteTarget?.full_name}
        onConfirm={handleDelete}
      />
    </div>
  );
}
